/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable react/require-default-props */
import React, { useEffect, useRef, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import CurrencyInput from 'react-currency-input-field';
import { BatchFinishBiddingData } from 'store/modules/management_store/actions';

interface SelectedBidding {
  id: number;
  edital: string;
  orgao?: string;
}

interface BatchFinishModalProps {
  modalOpened: boolean;
  setModalOpened: React.Dispatch<React.SetStateAction<boolean>>;
  selectedBiddings: Array<SelectedBidding>;
  totalOpenedTasks: number;
  isLoading: boolean;
  isLoaded: boolean;
  isError: boolean;
  onConfirm: (status: string, comments: string, biddings: Array<BatchFinishBiddingData>) => void;
  onSuccess?: () => void;
  errorMessage?: string;
  confirmBlocked?: boolean;
  children?: React.ReactNode;
}

const STATUS_OPTIONS = [
  { value: 'winner', label: 'Vencedor em todos os itens' },
  { value: 'partial_winner', label: 'Vencedor em alguns itens' },
  { value: 'lost', label: 'Nenhum item ganho' },
  { value: 'cancelled', label: 'A licitação foi anulada' },
  { value: 'failed', label: 'A licitação foi fracassada' },
  { value: 'revoked', label: 'A licitação foi revogada' },
  { value: 'declined', label: 'Declinamos da participação' },
];

const BatchFinishModal: React.FC<BatchFinishModalProps> = ({
  modalOpened,
  setModalOpened,
  selectedBiddings,
  totalOpenedTasks,
  isLoading,
  isLoaded,
  isError,
  onConfirm,
  onSuccess,
  errorMessage = 'Erro ao finalizar licitações.',
  confirmBlocked = false,
  children,
}) => {
  const [statusSelected, setStatusSelected] = useState<string>('');
  const [comments, setComments] = useState<string>('');
  const [amounts, setAmounts] = useState<Record<number, string | undefined>>({});

  const [actionDispatched, setActionDispatched] = useState<boolean>(false);
  const [showSuccess, setShowSuccess] = useState<boolean>(false);
  const [showError, setShowError] = useState<boolean>(false);
  const wasLoadingRef = useRef<boolean>(false);

  const showAmount = statusSelected === 'winner' || statusSelected === 'partial_winner';
  const isProcessing = actionDispatched && isLoading;
  const canSubmit = !!statusSelected && !isProcessing && !showSuccess && !confirmBlocked;

  useEffect(() => {
    if (!actionDispatched) return;
    if (isLoading) {
      wasLoadingRef.current = true;
      return;
    }
    if (!wasLoadingRef.current) return;
    wasLoadingRef.current = false;

    if (isLoaded) {
      setShowSuccess(true);
    } else if (isError) {
      setShowError(true);
    }
  }, [actionDispatched, isLoading, isLoaded, isError]);

  const handleConfirm = () => {
    const biddings: Array<BatchFinishBiddingData> = selectedBiddings.map((b) => ({
      id: b.id,
      amount: showAmount ? amounts[b.id] : undefined,
    }));
    setShowError(false);
    onConfirm(statusSelected, comments, biddings);
    setActionDispatched(true);
  };

  const closeModal = () => {
    if (showSuccess && onSuccess) onSuccess();
    setModalOpened(false);
  };

  return (
    <Modal show={modalOpened} size="lg">
      <div className="modal-content">
        <div className="modal-header">
          <h6 className="modal-title">
            Finalizar {selectedBiddings.length}{' '}
            {selectedBiddings.length === 1 ? 'licitação' : 'licitações'}
          </h6>
          <button aria-label="Close" className="close" type="button" onClick={closeModal}>
            <span aria-hidden="true">×</span>
          </button>
        </div>
        <div className="modal-body">
          {showError && <div className="my-2 alert alert-danger text-center">{errorMessage}</div>}
          {showSuccess && (
            <div className="my-2 alert alert-success text-center">
              Licitações finalizadas com sucesso!
            </div>
          )}
          <div className="mb-3">
            <label className="font-weight-bold">Status final aplicado a todas</label>
            {STATUS_OPTIONS.map((option, idx) => (
              <div className="custom-control custom-radio" key={option.value}>
                <input
                  className="custom-control-input"
                  type="radio"
                  name="batch_finish_status"
                  id={`batch-finish-radio-${idx}`}
                  checked={statusSelected === option.value}
                  onChange={() => setStatusSelected(option.value)}
                />
                <label className="custom-control-label" htmlFor={`batch-finish-radio-${idx}`}>
                  {option.label}
                </label>
              </div>
            ))}
          </div>
          {showAmount && (
            <div className="mb-3">
              <label className="font-weight-bold">Valor ganho por licitação</label>
              <div className="table-responsive" style={{ maxHeight: '240px', overflowY: 'auto' }}>
                <table className="table table-sm">
                  <thead>
                    <tr>
                      <th>Edital</th>
                      <th style={{ width: '180px' }}>Valor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedBiddings.map((bidding) => (
                      <tr key={bidding.id}>
                        <td>
                          <div className="font-weight-bold">{bidding.edital}</div>
                          {bidding.orgao && <small className="text-muted">{bidding.orgao}</small>}
                        </td>
                        <td>
                          <CurrencyInput
                            className="form-control form-control-sm"
                            id={`amount-${bidding.id}`}
                            name={`amount-${bidding.id}`}
                            placeholder="R$ 0,00"
                            prefix="R$ "
                            decimalsLimit={2}
                            value={amounts[bidding.id] ?? ''}
                            onValueChange={(value) =>
                              setAmounts((prev) => ({ ...prev, [bidding.id]: value }))
                            }
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          <div className="mb-3">
            <label htmlFor="batch_finish_comments" className="font-weight-bold">
              Observações (aplicadas a todas)
            </label>
            <textarea
              id="batch_finish_comments"
              name="batch_finish_comments"
              className="form-control"
              value={comments}
              onChange={(event) => setComments(event.target.value)}
            />
          </div>
          {totalOpenedTasks > 0 && (
            <div className="alert alert-warning" role="alert">
              {totalOpenedTasks === 1
                ? '1 tarefa em aberto será marcada como concluída.'
                : `${totalOpenedTasks} tarefas em aberto serão marcadas como concluídas.`}
            </div>
          )}
          {children}
        </div>
        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-md btn-secondary"
            disabled={isProcessing}
            onClick={closeModal}
          >
            {showSuccess ? 'Fechar' : 'Cancelar'}
          </button>
          {!showSuccess && (
            <button
              type="button"
              className="btn btn-md btn-primary"
              disabled={!canSubmit}
              onClick={handleConfirm}
            >
              Finalizar{' '}
              {selectedBiddings.length === 1
                ? 'licitação'
                : `${selectedBiddings.length} licitações`}
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default BatchFinishModal;
